// /remove/: ask for a recording to be removed (ARCHITECTURE.md section 5 requestRemoval).
// The server never says whether the reference code matched a recording, and neither does this
// page: success shows only the new request's own reference code.
// Logs (component "removal"): page_ready, not_configured, form_start, reference_normalized,
// validation_failed, submit_start, submit_offline, submit_ok, submit_failed, fallback_email.
import { logger } from '../lib/log.js';
import { formValues } from '../lib/forms.js';
import * as C from './common.js';

const log = logger('removal');
const REF_RE = /^OH-[0-9A-HJKMNP-TV-Z]{4}-[0-9A-HJKMNP-TV-Z]{4}$/;

/**
 * What a person typed -> "OH-XXXX-XXXX", or null if it cannot be a code. Same rules as the
 * server (functions/src/refcode.js): any case, spaces or dashes anywhere, the OH prefix optional,
 * and the look-alikes Crockford base32 allows (O -> 0, I and L -> 1).
 */
export function normalizeReference(input) {
  if (typeof input !== 'string') return null;
  let s = input.toUpperCase().replace(/[\s\-_.]/g, '');
  if (s.startsWith('OH') && s.length === 10) s = s.slice(2);
  if (s.length !== 8) return null;
  s = s.replace(/O/g, '0').replace(/[IL]/g, '1');
  const code = `OH-${s.slice(0, 4)}-${s.slice(4)}`;
  return REF_RE.test(code) ? code : null;
}

const BAD_CODE = 'That reference code does not look right. It looks like OH-7K3M-Q9TD. If you do not have it, leave this blank and tell us more below.';

export function init() {
  const root = document.querySelector('[data-remove]');
  if (!root) return;
  if (!C.isBackendConfigured()) {
    log.info('not_configured', {});
    C.trackMailtos(root, log, 'not_configured');
    return;
  }
  const form = root.querySelector('[data-form]');
  const success = root.querySelector('[data-success]');
  const submitBtn = form.querySelector('[data-submit]');
  const refInput = form.querySelector('#submission-reference');
  let sending = false;

  // Tidy the code as soon as they leave the box, so they can see what we will look up.
  refInput.addEventListener('blur', () => {
    const raw = refInput.value.trim();
    if (!raw) return;
    const code = normalizeReference(raw);
    if (code && code !== raw) {
      refInput.value = code;
      log.info('reference_normalized', {});
    }
  });

  C.trackStart(form, log);
  C.trackMailtos(root, log, 'page');

  function read() {
    const v = formValues(form);
    return { reference: v.submissionReference, name: v.name, email: v.email, phone: v.phone, details: v.details };
  }

  function validate(d) {
    const errs = [];
    if (d.reference && !normalizeReference(d.reference)) errs.push({ field: 'submissionReference', message: BAD_CODE });
    if (!d.name) errs.push({ field: 'name', message: 'Please tell us your name.' });
    errs.push(...C.contactErrors({
      email: d.email, phone: d.phone, emailField: 'email', phoneField: 'phone',
      missingMessage: 'Please give us your email address or your phone number, so we can confirm with you before anything is deleted.',
    }));
    return errs;
  }

  function fallbackMailto(d) {
    return C.mailtoHref(C.projectEmail(root), 'Please remove my St. Theresa Oral History recording', C.mailBody(
      'Hello,\n\nPlease remove my recording from the St. Theresa Oral History Project. I tried the form on the website, but it did not go through.',
      [
        ['My reference code', d.reference ? (normalizeReference(d.reference) || d.reference) : ''],
        ['My name', d.name],
        ['My email', d.email],
        ['My phone', d.phone],
        ['Details', d.details],
      ],
    ));
  }

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (sending) return;
    C.hideSubmitError(root);
    const d = read();
    const errs = validate(d);
    if (errs.length) {
      log.warn('validation_failed', { fields: errs.map((x) => x.field) });
      C.showErrors(form, errs);
      return;
    }
    C.clearErrors(form);
    const reference = d.reference ? normalizeReference(d.reference) : null;
    if (reference) refInput.value = reference;
    if (C.isOffline()) {
      log.warn('submit_offline', {});
      C.showSubmitError(root, { message: C.pageError({ code: 'oh/offline' }), mailto: fallbackMailto(d), log });
      return;
    }
    sending = true;
    const restore = C.busy(submitBtn, 'Sending…');
    log.info('submit_start', { hasReference: !!reference, hasDetails: !!d.details });
    try {
      const { ensureAnonymousUser, callFunction } = await C.backend();
      await ensureAnonymousUser();
      const res = await callFunction('requestRemoval', {
        logSessionId: C.sessionId(),
        submissionReference: reference,
        name: d.name,
        email: d.email || null,
        phone: d.phone || null,
        details: d.details || null,
      });
      log.info('submit_ok', { requestId: res.requestId, referenceCode: res.referenceCode });
      C.showSuccess(success, { referenceCode: res.referenceCode, hideEls: [form] });
    } catch (err) {
      C.logSubmitFailure(log, err);
      C.handleSubmitError(root, form, err, { mailto: fallbackMailto(d), log });
    } finally {
      restore();
      sending = false;
    }
  });

  log.info('page_ready', {});
}
